import React, { useState } from "react";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { firestore } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import Footer from "../shared/Footer";
import "../styles/portal.css";

/**
 * Retailer inquiry form.
 *
 * First pass, deliberately rudimentary - the client is still working out what
 * the wholesale program actually is, and a concrete form is the fastest way to
 * get them to a requirements conversation. Expect the field list to change.
 *
 * Writes one document per submission to `retailerInquiries`:
 *
 *   retailerInquiries/{autoId} {
 *     businessName, contactName, contactEmail, contactPhone,
 *     businessAddress, taxId, website, requestedTitles, notes,  // all string
 *     submittedByUid: string,   // "" when the sender is not signed in
 *     status: "new",            // pinned by the rule; workflow states TBD
 *     createdAt: serverTimestamp()
 *   }
 *
 * The matching `match /retailerInquiries/{inquiryId}` block in firestore.rules
 * validates this shape on create, so the two must be changed together - and the
 * rule must be DEPLOYED before this page can save anything (SHARED.md §10:
 * new collections deny by default).
 */

const EMPTY_FORM = {
  businessName: "",
  contactName: "",
  contactEmail: "",
  contactPhone: "",
  businessAddress: "",
  taxId: "",
  website: "",
  requestedTitles: "",
  notes: "",
};

export default function RetailersPage() {
  const { user } = useAuth();

  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const setField = (name) => (e) =>
    setForm((prev) => ({ ...prev, [name]: e.target.value }));

  async function handleSubmit(e) {
    e.preventDefault();

    if (!form.businessName.trim() || !form.contactName.trim() || !form.contactEmail.trim()) {
      setError("Business name, contact name, and email are required.");
      return;
    }

    try {
      setSubmitting(true);
      setError("");

      await addDoc(collection(firestore, "retailerInquiries"), {
        businessName: form.businessName.trim(),
        contactName: form.contactName.trim(),
        contactEmail: form.contactEmail.trim(),
        contactPhone: form.contactPhone.trim(),
        businessAddress: form.businessAddress.trim(),
        taxId: form.taxId.trim(),
        website: form.website.trim(),
        requestedTitles: form.requestedTitles.trim(),
        notes: form.notes.trim(),
        submittedByUid: user?.uid || "",
        status: "new",
        createdAt: serverTimestamp(),
      });

      setForm(EMPTY_FORM);
      setSubmitted(true);
    } catch (err) {
      console.error("Retailer inquiry submission failed:", err);
      setError(
        "We could not send your inquiry. Please try again, or email us directly at retail@antarcticpress.com."
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={{ width: "100%", margin: 0, padding: 0, overflowX: "hidden", backgroundColor: "#0d1b2a", minHeight: "100vh" }}>

      {/* Hero Banner */}
      <div style={heroBannerStyle}>
        <div style={heroContentStyle}>
          <h1 style={heroTitleStyle}>Retailers</h1>
          <p style={heroSubheadStyle}>
            Carry Antarctic Press in your store. Tell us about your business and what you'd like to stock.
          </p>
        </div>
      </div>

      {/* Page content */}
      <div style={pageBodyStyle}>

        <RetailerCard title="Wholesale Inquiries">
          <p style={bodyTextStyle}>
            Antarctic Press works with comic shops, bookstores, conventions, and specialty
            retailers to get physical copies onto shelves. Fill out the form below with your
            business details and the titles you're interested in, and our sales team will
            follow up with pricing, terms, and availability.
          </p>
          <p style={bodyTextStyle}>
            A valid tax ID or resale certificate number is required to open a wholesale
            account. If you don't have one yet, submit the form anyway and note it below —
            we'll walk you through it.
          </p>
        </RetailerCard>

        <RetailerCard title="Retailer Contact Form">
          {submitted ? (
            <div style={successStyle}>
              <strong>Thanks — we've got your inquiry.</strong>
              <p style={{ ...bodyTextStyle, margin: "0.5rem 0 1rem 0" }}>
                Someone from Antarctic Press will reach out to the email address you provided.
              </p>
              <button
                type="button"
                style={secondaryButtonStyle}
                onClick={() => setSubmitted(false)}
              >
                Submit another inquiry
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} style={formStyle}>

              {error ? <div style={errorStyle}>{error}</div> : null}

              <h3 style={fieldGroupTitleStyle}>Business Information</h3>

              <label style={labelStyle}>
                Business Name *
                <input
                  type="text"
                  value={form.businessName}
                  onChange={setField("businessName")}
                  style={inputStyle}
                  disabled={submitting}
                />
              </label>

              <div style={fieldRowStyle}>
                <label style={labelStyle}>
                  Contact Name *
                  <input
                    type="text"
                    value={form.contactName}
                    autoComplete="name"
                    onChange={setField("contactName")}
                    style={inputStyle}
                    disabled={submitting}
                  />
                </label>

                <label style={labelStyle}>
                  Email *
                  <input
                    type="email"
                    value={form.contactEmail}
                    autoComplete="email"
                    onChange={setField("contactEmail")}
                    style={inputStyle}
                    disabled={submitting}
                  />
                </label>
              </div>

              <div style={fieldRowStyle}>
                <label style={labelStyle}>
                  Phone
                  <input
                    type="tel"
                    value={form.contactPhone}
                    autoComplete="tel"
                    onChange={setField("contactPhone")}
                    style={inputStyle}
                    disabled={submitting}
                  />
                </label>

                <label style={labelStyle}>
                  Website / Storefront
                  <input
                    type="text"
                    value={form.website}
                    placeholder="https://"
                    onChange={setField("website")}
                    style={inputStyle}
                    disabled={submitting}
                  />
                </label>
              </div>

              <label style={labelStyle}>
                Business Address
                <textarea
                  value={form.businessAddress}
                  rows={3}
                  placeholder={"Street\nCity, State ZIP"}
                  onChange={setField("businessAddress")}
                  style={textareaStyle}
                  disabled={submitting}
                />
              </label>

              <label style={labelStyle}>
                Tax ID / Resale Certificate Number
                <input
                  type="text"
                  value={form.taxId}
                  onChange={setField("taxId")}
                  style={inputStyle}
                  disabled={submitting}
                />
                <span style={helpTextStyle}>
                  Required to open a wholesale account. Leave blank if you don't have one yet.
                </span>
              </label>

              <h3 style={fieldGroupTitleStyle}>Physical Copy Request</h3>

              <label style={labelStyle}>
                Titles &amp; Quantities
                <textarea
                  value={form.requestedTitles}
                  rows={4}
                  placeholder="e.g. Gold Digger #300 — 25 copies&#10;Ninja High School TPB Vol. 1 — 10 copies"
                  onChange={setField("requestedTitles")}
                  style={textareaStyle}
                  disabled={submitting}
                />
              </label>

              <label style={labelStyle}>
                Anything Else?
                <textarea
                  value={form.notes}
                  rows={3}
                  placeholder="Event dates, standing order interest, questions about terms..."
                  onChange={setField("notes")}
                  style={textareaStyle}
                  disabled={submitting}
                />
              </label>

              <button type="submit" style={primaryButtonStyle} disabled={submitting}>
                {submitting ? "Sending..." : "Send Inquiry"}
              </button>

              <p style={helpTextStyle}>
                * Required. We'll only use this information to contact you about your
                wholesale account.
              </p>
            </form>
          )}
        </RetailerCard>

        <RetailerCard title="Prefer to Talk to a Person?">
          <p style={bodyTextStyle}>
            You can also reach our sales team by mail:
          </p>
          <div style={addressBlockStyle}>
            <strong>Antarctic Press</strong><br />
            Attn: Retail Sales<br />
            4334 Parkwood Dr<br />
            San Antonio, TX 78218
          </div>
        </RetailerCard>

      </div>
      <Footer style={{ marginTop: 0 }} />
    </div>
  );
}

function RetailerCard({ title, children }) {
  return (
    <div style={cardStyle}>
      <h2 style={cardTitleStyle}>{title}</h2>
      <div>{children}</div>
    </div>
  );
}

const heroBannerStyle = {
  background: "linear-gradient(135deg, #0d1b2a 0%, #114c96 100%)",
  padding: "64px 16px 56px",
  borderBottom: "3px solid #43c6f9",
};

const heroContentStyle = {
  maxWidth: "960px",
  margin: "0 auto",
};

const heroTitleStyle = {
  color: "#ffffff",
  fontWeight: 900,
  fontSize: "2.8rem",
  margin: "0 0 0.5rem 0",
  letterSpacing: "0.02em",
};

const heroSubheadStyle = {
  color: "#43c6f9",
  fontSize: "1.15rem",
  margin: 0,
  fontWeight: 500,
};

const pageBodyStyle = {
  maxWidth: "960px",
  margin: "0 auto",
  padding: "40px 16px 24px",
};

const cardStyle = {
  backgroundColor: "#ffffff",
  border: "1px solid #d7e2ef",
  borderTop: "4px solid #114c96",
  borderRadius: "12px",
  padding: "2rem 2rem 1.5rem",
  marginBottom: "24px",
  boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
};

const cardTitleStyle = {
  color: "#114c96",
  fontWeight: 800,
  fontSize: "1.35rem",
  margin: "0 0 1rem 0",
};

const bodyTextStyle = {
  color: "#334155",
  lineHeight: 1.75,
  margin: "0 0 0.75rem 0",
};

const addressBlockStyle = {
  backgroundColor: "#f0f6ff",
  border: "1px solid #c9daf8",
  borderRadius: "8px",
  padding: "1rem 1.25rem",
  color: "#1e3a5f",
  lineHeight: 1.9,
  fontFamily: "monospace",
  fontSize: "0.95rem",
};

const formStyle = {
  display: "flex",
  flexDirection: "column",
  gap: "1rem",
};

// The two-up row collapses to one column under 640px via flexWrap plus a
// minWidth on the labels, so no media query (and no new CSS class) is needed.
const fieldRowStyle = {
  display: "flex",
  flexWrap: "wrap",
  gap: "1rem",
};

const labelStyle = {
  display: "flex",
  flexDirection: "column",
  gap: "0.4rem",
  color: "#111827",
  fontWeight: 700,
  flex: "1 1 260px",
  minWidth: "260px",
};

const fieldGroupTitleStyle = {
  color: "#114c96",
  fontSize: "1rem",
  fontWeight: 800,
  textTransform: "uppercase",
  letterSpacing: "0.08em",
  borderBottom: "1px solid #d7e2ef",
  paddingBottom: "0.5rem",
  margin: "0.5rem 0 0 0",
};

const inputStyle = {
  padding: "0.8rem 0.9rem",
  border: "1px solid #cbd5e1",
  borderRadius: "8px",
  fontSize: "1rem",
  width: "100%",
};

const textareaStyle = {
  ...inputStyle,
  fontFamily: "inherit",
  resize: "vertical",
};

const helpTextStyle = {
  color: "#64748b",
  fontWeight: 400,
  fontSize: "0.85rem",
  lineHeight: 1.5,
};

const primaryButtonStyle = {
  marginTop: "0.5rem",
  background: "#114c96",
  color: "#fff",
  border: "none",
  borderRadius: "8px",
  padding: "0.85rem 1rem",
  fontWeight: 800,
  fontSize: "1rem",
  cursor: "pointer",
};

const secondaryButtonStyle = {
  background: "#fff",
  color: "#114c96",
  border: "1px solid #114c96",
  borderRadius: "8px",
  padding: "0.7rem 1.1rem",
  fontWeight: 800,
  cursor: "pointer",
};

const errorStyle = {
  background: "#fff1f2",
  color: "#be123c",
  border: "1px solid #fecdd3",
  borderRadius: "8px",
  padding: "0.85rem",
  fontWeight: 700,
};

const successStyle = {
  background: "#f0fdf4",
  color: "#166534",
  border: "1px solid #bbf7d0",
  borderRadius: "8px",
  padding: "1.25rem",
};
